import {
  CSSProperties,
  ReactNode,
  RefObject,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useRef } from 'react';
import { VariantProps, tv } from 'tailwind-variants';

import Text from '@cartwhl/ui/base/Text';
import useElementSize from '@cartwhl/ui/hooks/useElementSize';

import CrossIcon from '../icons/CrossIcon';

const CONTAINER_SIZE_DURATION = 200;
const FADE_CONTENT_IN_DURATION = 300;
const FADE_CONTENT_OUT_DURATION = 150;

// #region Context

interface FrontDoorContextType {
  containerTransitionDelay: number;
  isUnmounting: boolean;
  setContainerTransitionDelay: (delay: number) => void;
  setIsUnmounting: (isUnmounting: boolean) => void;
}

const FrontDoorContext = createContext<FrontDoorContextType | undefined>(
  undefined,
);

function useFrontDoorContext() {
  const context = useContext(FrontDoorContext);
  if (context === undefined) {
    throw new Error(
      'useFrontDoorContext must be used within a FrontDoorProvider',
    );
  }
  return context;
}

// #endregion Context

// #region Container

export function Container({
  children,
  showX,
  onXClick,
  ref,
  className,
}: {
  children: ReactNode;
  showX?: boolean;
  onXClick?: () => void;
  ref?: RefObject<HTMLDivElement | null>;
  className?: string;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [containerTransitionDelay, setContainerTransitionDelay] = useState(0);
  const [isUnmounting, setIsUnmounting] = useState(false);

  const { width: contentWidth, height: contentHeight } =
    useElementSize(contentRef);

  return (
    <FrontDoorContext.Provider
      value={{
        containerTransitionDelay,
        setContainerTransitionDelay,
        isUnmounting,
        setIsUnmounting,
      }}
    >
      <div
        className={`pointer-events-none flex h-full w-full flex-col items-center justify-end ${className}`}
        ref={ref}
      >
        <div className="pointer-events-auto relative z-0 w-fit max-w-full lg:w-fit">
          <div
            className="dark:bg-gray-75/45 absolute bottom-0 left-1/2 z-0 -translate-x-1/2 rounded-2xl bg-white/25 backdrop-blur-md transition-all lg:rounded-[24px]"
            style={{
              width: `${contentWidth}px`,
              height: `${contentHeight}px`,
              transitionDuration: `${CONTAINER_SIZE_DURATION}ms`,
              transitionDelay: `${containerTransitionDelay}ms`,
              boxShadow:
                '2px 2px 4px 0 rgba(0, 0, 0, 0.04), 8px 16px 24px 0 rgba(0, 0, 0, 0.04), 8px 32px 64px 0 rgba(0, 0, 0, 0.10)',
            }}
          />
          <div
            className="relative z-10 flex min-w-[280px] flex-col items-center gap-2 p-2 lg:min-w-0"
            ref={contentRef}
          >
            {children}

            {showX && (
              <div className="self-stretch lg:hidden">
                <Button text="Back" className="w-full" onClick={onXClick} />
              </div>
            )}
          </div>
          <div
            className={`absolute bottom-0 left-full hidden px-2 py-2 transition-all lg:block ${showX ? 'visible opacity-100' : 'invisible opacity-0'}`}
          >
            <Button
              variant="secondary"
              icon={<CrossIcon />}
              className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-2xl bg-white/25 transition-colors hover:bg-white/50"
              onClick={onXClick}
            />
          </div>
        </div>
      </div>
    </FrontDoorContext.Provider>
  );
}

// #endregion Container

// #region FadeContent

const FadeContent = ({
  show,
  children,
  className,
}: {
  show: boolean;
  children: ReactNode;
  className?: string;
}) => {
  const [mounted, setMounted] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const { setContainerTransitionDelay, setIsUnmounting, isUnmounting } =
    useFrontDoorContext();

  const initialMounted = useRef(false);

  useEffect(() => {
    const update = async () => {
      if (show) {
        await delay(FADE_CONTENT_OUT_DURATION);
        setContainerTransitionDelay(0);
        setMounted(true);
        await delay(FADE_CONTENT_IN_DURATION);
        setOpacity(1);
      } else if (!show && initialMounted.current) {
        setIsUnmounting(true);
        setOpacity(0);
        setContainerTransitionDelay(FADE_CONTENT_OUT_DURATION);
        setMounted(true);
        await delay(FADE_CONTENT_OUT_DURATION);
        setMounted(false);
        setIsUnmounting(false);
      }
    };

    update();

    initialMounted.current = true;
  }, [show]);

  if (!mounted) return null;

  return (
    <div
      className={`transition-all ${className}`}
      style={{
        opacity,
        transform: opacity ? `translateY(0px)` : `translateY(6px)`,
        transitionDuration: `${show ? FADE_CONTENT_IN_DURATION : FADE_CONTENT_OUT_DURATION}ms`,
      }}
    >
      {children}
    </div>
  );
};

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// #endregion FadeContent

// #region Button

const button = tv({
  base: 'text-gray-90 flex h-10 min-w-0 items-center justify-center gap-2 rounded-lg pt-[0.15em] transition-all duration-150 active:scale-[0.98] dark:text-white',
  variants: {
    variant: {
      go: 'bg-green/80 dark:bg-green/60',
      primary: 'dark:bg-gray-90/55 bg-white/55',
      secondary: 'dark:bg-gray-90/55 bg-white/25',
    },
    padding: {
      textOnly: 'px-4',
      iconOnly: 'px-2.5',
      iconAndText: 'px-4',
      wide: 'pl-[22px] pr-6',
    },
    isActive: {
      true: 'bg-white',
      false: '',
    },
    rounded: {
      first: 'lg:rounded-l-2xl',
      last: 'lg:rounded-r-2xl',
      both: 'lg:rounded-2xl',
    },
    disabled: {
      true: 'opacity-50',
      false: '',
    },
  },
  compoundVariants: [
    // Hover styles:
    {
      variant: ['primary', 'secondary'],
      disabled: false,
      className: 'dark:hover:bg-gray-90 cursor-pointer hover:bg-white',
    },
    {
      variant: 'go',
      disabled: false,
      className: 'hover:bg-green dark:hover:bg-green/80 cursor-pointer',
    },
  ],
  defaultVariants: {
    variant: 'primary',
    disabled: false,
  },
});

export function Button({
  text,
  icon,
  iconEnd,
  onClick,
  isActive,
  className,
  variant,
  rounded,
  isWide,
  style,
  disabled,
}: {
  text?: string;
  icon?: ReactNode;
  iconEnd?: ReactNode;
  onClick?: () => void;
  isActive?: boolean;
  className?: string;
  variant?: VariantProps<typeof button>['variant'];
  rounded?: VariantProps<typeof button>['rounded'];
  isWide?: boolean;
  style?: CSSProperties;
  disabled?: boolean;
}) {
  return (
    <Text
      as="button"
      variant="studio/body/medium/sm"
      className={button({
        variant,
        padding: getButtonPadding(text, icon, iconEnd, isWide),
        rounded,
        isActive,
        disabled,
        className,
      })}
      style={style}
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      {text && <span className="truncate">{text}</span>}
      {iconEnd}
    </Text>
  );
}

function getButtonPadding(
  text?: string,
  icon?: ReactNode,
  iconEnd?: ReactNode,
  isWide?: boolean,
) {
  if (isWide) return 'wide';
  if (text && (icon || iconEnd)) return 'iconAndText';
  if (text) return 'textOnly';
  return 'iconOnly';
}

// #endregion Button

// #region Divider

const Divider = ({ className }: { className?: string }) => {
  return (
    <hr className={`mx-2 h-10 w-px flex-none border-0 bg-white ${className}`} />
  );
};

// #endregion Divider

export default {
  Container,
  FadeContent,
  Button,
  Divider,
};
